import type { WordCloudBasis, WordCloudItem } from '../types';

const getWordCloudPrefix = (basis?: WordCloudBasis) => {
  switch (basis) {
    case 'hashtags':
      return '#';
    case 'mentions':
      return '@';
    default:
      return '';
  }
};

/**
 * 워드클라우드 아이템들의 count 값을 sqrt 기반 연속 스케일로 변환합니다.
 * @param words - 워드클라우드 아이템 배열
 * @param minScale - 스케일링 최소값
 * @param maxScale - 스케일링 최대값
 * @param basis - 워드클라우드 기준 ('keywords' | 'hashtags') - hashtags일 때 # 접두사 추가
 * @returns count 차이를 유지한 연속 스케일 값을 가진 새로운 배열
 */
export function scaleWordCloudValues(
  words: WordCloudItem[],
  minScale: number = 8,
  maxScale: number = 1500,
  basis?: WordCloudBasis,
): Array<{ text: string; value: number; originalKeyword?: string }> {
  if (words.length === 0) {
    return [];
  }

  const counts = words.map((word) => word.count);
  const minCount = Math.min(...counts);
  const maxCount = Math.max(...counts);
  const minRoot = Math.sqrt(Math.max(minCount, 0));
  const maxRoot = Math.sqrt(Math.max(maxCount, 0));
  const scaleRange = Math.max(maxScale - minScale, 0);
  const hasRange = maxRoot > minRoot;

  return words.map((word) => {
    const rootCount = Math.sqrt(Math.max(word.count, 0));
    const normalized = hasRange ? (rootCount - minRoot) / (maxRoot - minRoot) : 1;
    const value = minScale + normalized * scaleRange;

    return {
      text: (() => {
        const prefix = getWordCloudPrefix(basis);
        if (!prefix || word.keyword.startsWith(prefix)) {
          return word.keyword;
        }
        return `${prefix}${word.keyword}`;
      })(),
      value: value,
      originalKeyword: word.originalKeyword,
    };
  });
}
